<template>
  <div class="acamp-page">
    <h1 class="acamp-title">活動管理</h1>

    <!-- Tab 切換 -->
    <div class="acamp-tabs">
      <button class="acamp-tab" :class="{ 'acamp-tab--active': tab === 'coupon' }" @click="tab = 'coupon'">🏷️ 折扣券</button>
      <button class="acamp-tab" :class="{ 'acamp-tab--active': tab === 'gift' }" @click="tab = 'gift'">🎁 滿額贈</button>
    </div>

    <!-- ── 折扣券 Tab ── -->
    <div v-if="tab === 'coupon'">
      <div class="acamp-bar">
        <p class="acamp-hint">消費滿指定金額，享指定折數優惠，自動於購物車顯示。</p>
        <button class="acamp-btn acamp-btn--primary" @click="openAddCoupon">＋ 新增折扣券</button>
      </div>
      <div v-if="couponLoading" class="acamp-state">載入中…</div>
      <div v-else-if="coupons.length === 0" class="acamp-state">尚無折扣券，點右上角「＋ 新增折扣券」建立。</div>
      <table v-else class="acamp-table">
        <thead>
          <tr>
            <th>最低消費</th><th>折數</th><th>循環倒數</th><th>狀態</th><th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in coupons" :key="c.id">
            <td>NT${{ Number(c.min_amount).toLocaleString() }}</td>
            <td>{{ Number(c.discount_rate) }} 折</td>
            <td>
              <span v-if="c.cycle_days" class="acamp-cycle">循環 {{ c.cycle_days }} 天<span v-if="c.days_left != null" class="acamp-days-left"> ⏳{{ c.days_left }}天</span></span>
              <span v-else class="acamp-muted">永不過期</span>
            </td>
            <td><span class="acamp-badge" :class="c.is_active ? 'acamp-badge--on' : 'acamp-badge--off'">{{ c.is_active ? '啟用' : '停用' }}</span></td>
            <td class="acamp-actions">
              <button class="acamp-btn acamp-btn--sm acamp-btn--ghost" @click="openEditCoupon(c)">編輯</button>
              <button class="acamp-btn acamp-btn--sm acamp-btn--toggle" @click="toggleCoupon(c)">{{ c.is_active ? '停用' : '啟用' }}</button>
              <button class="acamp-btn acamp-btn--sm acamp-btn--danger" @click="deleteCoupon(c)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── 滿額贈 Tab ── -->
    <div v-if="tab === 'gift'">
      <div class="acamp-bar">
        <p class="acamp-hint">消費達門檻即自動贈送指定禮品，自動於購物車顯示。</p>
        <button class="acamp-btn acamp-btn--primary" @click="openAddGift">＋ 新增滿額贈</button>
      </div>
      <div v-if="giftLoading" class="acamp-state">載入中…</div>
      <div v-else-if="gifts.length === 0" class="acamp-state">尚無滿額贈活動，點右上角「＋ 新增滿額贈」建立。</div>
      <table v-else class="acamp-table">
        <thead>
          <tr>
            <th>門檻金額</th><th>贈品名稱</th><th>數量</th><th>累贈</th><th>狀態</th><th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="g in gifts" :key="g.id">
            <td>NT${{ Number(g.min_amount).toLocaleString() }}</td>
            <td>{{ g.gift_name }}</td>
            <td>{{ g.gift_quantity }} 件</td>
            <td><span class="acamp-muted">{{ g.is_cumulative ? '累贈' : '不累贈' }}</span></td>
            <td><span class="acamp-badge" :class="g.is_active ? 'acamp-badge--on' : 'acamp-badge--off'">{{ g.is_active ? '啟用' : '停用' }}</span></td>
            <td class="acamp-actions">
              <button class="acamp-btn acamp-btn--sm acamp-btn--ghost" @click="openEditGift(g)">編輯</button>
              <button class="acamp-btn acamp-btn--sm acamp-btn--toggle" @click="toggleGift(g)">{{ g.is_active ? '停用' : '啟用' }}</button>
              <button class="acamp-btn acamp-btn--sm acamp-btn--danger" @click="deleteGift(g)">刪除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ── 折扣券 Modal ── -->
    <teleport to="body">
      <div v-if="couponModal" class="acamp-overlay" @click.self="couponModal = false">
        <div class="acamp-modal">
          <div class="acamp-modal__header">
            <h2>{{ couponForm.id ? '編輯折扣券' : '新增折扣券' }}</h2>
            <button class="acamp-modal__close" @click="couponModal = false">✕</button>
          </div>
          <div class="acamp-modal__body">
            <div class="acamp-field">
              <label>最低消費金額（NT$）<span class="acamp-req">*</span></label>
              <input v-model.number="couponForm.min_amount" type="number" min="1" class="acamp-input" placeholder="例：500" />
              <p v-if="couponErr.min_amount" class="acamp-err">{{ couponErr.min_amount }}</p>
            </div>
            <div class="acamp-field">
              <label>折數 <span class="acamp-req">*</span><span class="acamp-hint-sm">（0.1~9.9，例：9.5 = 9.5折）</span></label>
              <div class="acamp-row">
                <input v-model.number="couponForm.discount_rate" type="number" min="0.1" max="9.9" step="0.1" class="acamp-input" style="flex:1" placeholder="例：9.5" />
                <span class="acamp-suffix">折</span>
              </div>
              <p v-if="couponErr.discount_rate" class="acamp-err">{{ couponErr.discount_rate }}</p>
            </div>
            <div class="acamp-field">
              <label>循環天數（選填）<span class="acamp-hint-sm">空白 = 永不過期</span></label>
              <div class="acamp-row">
                <input v-model.number="couponForm.cycle_days" type="number" min="1" class="acamp-input" style="flex:1" placeholder="例：7" />
                <span class="acamp-suffix">天</span>
              </div>
            </div>
            <label class="acamp-check"><input type="checkbox" v-model="couponForm.is_active" /> 立即啟用</label>
            <div v-if="couponPreviewValid" class="acamp-preview">
              消費滿 NT${{ Number(couponForm.min_amount).toLocaleString() }} 享 {{ couponForm.discount_rate }} 折
              <span class="acamp-preview-sub">（消費 NT${{ Number(couponForm.min_amount).toLocaleString() }} → 券後 NT${{ couponAfter.toLocaleString() }}，省 NT${{ couponSave.toLocaleString() }}）</span>
            </div>
            <p v-if="couponApiErr" class="acamp-err">{{ couponApiErr }}</p>
          </div>
          <div class="acamp-modal__footer">
            <button class="acamp-btn acamp-btn--ghost" @click="couponModal = false">取消</button>
            <button class="acamp-btn acamp-btn--primary" :disabled="couponSaving" @click="saveCoupon">{{ couponSaving ? '儲存中…' : '儲存' }}</button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── 滿額贈 Modal ── -->
    <teleport to="body">
      <div v-if="giftModal" class="acamp-overlay" @click.self="giftModal = false">
        <div class="acamp-modal">
          <div class="acamp-modal__header">
            <h2>{{ giftForm.id ? '編輯滿額贈' : '新增滿額贈' }}</h2>
            <button class="acamp-modal__close" @click="giftModal = false">✕</button>
          </div>
          <div class="acamp-modal__body">
            <div class="acamp-field">
              <label>門檻金額（NT$）<span class="acamp-req">*</span></label>
              <input v-model.number="giftForm.min_amount" type="number" min="1" class="acamp-input" placeholder="例：2000" />
              <p v-if="giftErr.min_amount" class="acamp-err">{{ giftErr.min_amount }}</p>
            </div>
            <div class="acamp-field">
              <label>贈品名稱<span class="acamp-req">*</span></label>
              <input v-model="giftForm.gift_name" type="text" class="acamp-input" placeholder="例：潔顏慕斯 150ml" />
              <p v-if="giftErr.gift_name" class="acamp-err">{{ giftErr.gift_name }}</p>
            </div>
            <div class="acamp-field">
              <label>數量<span class="acamp-req">*</span></label>
              <div class="acamp-row">
                <input v-model.number="giftForm.gift_quantity" type="number" min="1" class="acamp-input" style="flex:1" placeholder="1" />
                <span class="acamp-suffix">件</span>
              </div>
            </div>
            <label class="acamp-check">
              <input type="checkbox" v-model="giftForm.is_cumulative" />
              累贈（勾選：消費 4,000 送 2 件；不勾：不論消費多少只送 1 次）
            </label>
            <label class="acamp-check"><input type="checkbox" v-model="giftForm.is_active" /> 立即啟用</label>
            <div v-if="giftForm.min_amount && giftForm.gift_name" class="acamp-preview">
              🎁 消費滿 NT${{ Number(giftForm.min_amount).toLocaleString() }} 贈 {{ giftForm.gift_name }} × {{ giftForm.gift_quantity }}
              <span class="acamp-preview-sub">{{ giftForm.is_cumulative ? '（累贈）' : '（不累贈）' }}</span>
            </div>
            <p v-if="giftApiErr" class="acamp-err">{{ giftApiErr }}</p>
          </div>
          <div class="acamp-modal__footer">
            <button class="acamp-btn acamp-btn--ghost" @click="giftModal = false">取消</button>
            <button class="acamp-btn acamp-btn--primary" :disabled="giftSaving" @click="saveGift">{{ giftSaving ? '儲存中…' : '儲存' }}</button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const tab = ref('coupon')

// ── 折扣券 ────────────────────────────────────────────
const coupons      = ref([])
const couponLoading = ref(false)
const couponModal  = ref(false)
const couponSaving = ref(false)
const couponApiErr = ref('')
const couponErr    = ref({})
const couponForm   = ref({ id: null, min_amount: '', discount_rate: '', cycle_days: '', is_active: true })

const couponPreviewValid = computed(() => {
  const r = Number(couponForm.value.discount_rate)
  return couponForm.value.min_amount > 0 && r > 0 && r < 10
})
const couponAfter = computed(() => Math.round(Number(couponForm.value.min_amount) * Number(couponForm.value.discount_rate) / 10))
const couponSave  = computed(() => Number(couponForm.value.min_amount) - couponAfter.value)

async function fetchCoupons() {
  couponLoading.value = true
  try {
    const res = await axios.get('/api/admin/coupons')
    if (res.data.success) coupons.value = res.data.coupons
  } finally { couponLoading.value = false }
}

function openAddCoupon() {
  couponForm.value = { id: null, min_amount: '', discount_rate: '', cycle_days: '', is_active: true }
  couponErr.value = {}; couponApiErr.value = ''; couponModal.value = true
}
function openEditCoupon(c) {
  couponForm.value = { id: c.id, min_amount: c.min_amount, discount_rate: Number(c.discount_rate), cycle_days: c.cycle_days || '', is_active: !!c.is_active }
  couponErr.value = {}; couponApiErr.value = ''; couponModal.value = true
}

async function saveCoupon() {
  const e = {}
  if (!couponForm.value.min_amount || couponForm.value.min_amount <= 0) e.min_amount = '請輸入有效金額'
  const r = Number(couponForm.value.discount_rate)
  if (isNaN(r) || r <= 0 || r >= 10) e.discount_rate = '折數需介於 0.1 ~ 9.9'
  couponErr.value = e
  if (Object.keys(e).length) return
  couponSaving.value = true; couponApiErr.value = ''
  try {
    const payload = { ...couponForm.value }
    const res = couponForm.value.id
      ? await axios.put(`/api/admin/coupons/${couponForm.value.id}`, payload)
      : await axios.post('/api/admin/coupons', payload)
    if (res.data.success) { couponModal.value = false; await fetchCoupons() }
    else couponApiErr.value = res.data.message || '儲存失敗'
  } catch (err) {
    couponApiErr.value = err.response?.data?.message || '伺服器錯誤'
  } finally { couponSaving.value = false }
}

async function toggleCoupon(c) {
  await axios.put(`/api/admin/coupons/${c.id}`, { min_amount: c.min_amount, discount_rate: c.discount_rate, cycle_days: c.cycle_days || null, is_active: c.is_active ? 0 : 1 })
  await fetchCoupons()
}

async function deleteCoupon(c) {
  if (!confirm(`確定刪除「滿 NT$${c.min_amount} 享 ${Number(c.discount_rate)} 折」嗎？`)) return
  await axios.delete(`/api/admin/coupons/${c.id}`)
  await fetchCoupons()
}

// ── 滿額贈 ────────────────────────────────────────────
const gifts       = ref([])
const giftLoading = ref(false)
const giftModal   = ref(false)
const giftSaving  = ref(false)
const giftApiErr  = ref('')
const giftErr     = ref({})
const giftForm    = ref({ id: null, min_amount: '', gift_name: '', gift_quantity: 1, is_cumulative: false, is_active: true })

async function fetchGifts() {
  giftLoading.value = true
  try {
    const res = await axios.get('/api/admin/threshold-gifts')
    if (res.data.success) gifts.value = res.data.gifts
  } finally { giftLoading.value = false }
}

function openAddGift() {
  giftForm.value = { id: null, min_amount: '', gift_name: '', gift_quantity: 1, is_cumulative: false, is_active: true }
  giftErr.value = {}; giftApiErr.value = ''; giftModal.value = true
}
function openEditGift(g) {
  giftForm.value = { id: g.id, min_amount: g.min_amount, gift_name: g.gift_name, gift_quantity: g.gift_quantity, is_cumulative: !!g.is_cumulative, is_active: !!g.is_active }
  giftErr.value = {}; giftApiErr.value = ''; giftModal.value = true
}

async function saveGift() {
  const e = {}
  if (!giftForm.value.min_amount || giftForm.value.min_amount <= 0) e.min_amount = '請輸入有效門檻金額'
  if (!giftForm.value.gift_name?.trim()) e.gift_name = '請輸入贈品名稱'
  giftErr.value = e
  if (Object.keys(e).length) return
  giftSaving.value = true; giftApiErr.value = ''
  try {
    const payload = { ...giftForm.value }
    const res = giftForm.value.id
      ? await axios.put(`/api/admin/threshold-gifts/${giftForm.value.id}`, payload)
      : await axios.post('/api/admin/threshold-gifts', payload)
    if (res.data.success) { giftModal.value = false; await fetchGifts() }
    else giftApiErr.value = res.data.message || '儲存失敗'
  } catch (err) {
    giftApiErr.value = err.response?.data?.message || '伺服器錯誤'
  } finally { giftSaving.value = false }
}

async function toggleGift(g) {
  await axios.put(`/api/admin/threshold-gifts/${g.id}`, { ...g, is_active: g.is_active ? 0 : 1 })
  await fetchGifts()
}

async function deleteGift(g) {
  if (!confirm(`確定刪除「滿 NT$${g.min_amount} 贈 ${g.gift_name}」嗎？`)) return
  await axios.delete(`/api/admin/threshold-gifts/${g.id}`)
  await fetchGifts()
}

onMounted(() => { fetchCoupons(); fetchGifts() })
</script>

<style scoped>
.acamp-page { max-width: 960px; margin: 0 auto; padding: 32px 20px; }
.acamp-title { font-size: 1.6rem; font-weight: 700; color: #2c3e50; margin: 0 0 20px; }

/* Tabs */
.acamp-tabs { display: flex; gap: 4px; border-bottom: 2px solid #e8ecf0; margin-bottom: 24px; }
.acamp-tab {
  padding: 10px 24px; border: none; background: none;
  font-size: 0.95rem; font-weight: 600; color: #7f8c8d;
  cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px;
  transition: color .15s, border-color .15s;
}
.acamp-tab--active { color: #2E3A59; border-bottom-color: #2E3A59; }
.acamp-tab:hover:not(.acamp-tab--active) { color: #444; }

.acamp-bar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.acamp-hint { font-size: 0.88rem; color: #7f8c8d; margin: 0; }
.acamp-hint-sm { font-size: 0.78rem; color: #aaa; font-weight: 400; margin-left: 4px; }
.acamp-state { text-align: center; color: #7f8c8d; padding: 40px 0; font-size: 0.95rem; }

/* Table */
.acamp-table { width: 100%; border-collapse: collapse; font-size: 0.92rem; }
.acamp-table th, .acamp-table td { padding: 10px 14px; border-bottom: 1px solid #eaeaea; text-align: left; vertical-align: middle; }
.acamp-table thead th { background: #f4f6f8; font-weight: 600; color: #555; }
.acamp-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.acamp-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 0.78rem; font-weight: 600; }
.acamp-badge--on  { background: #d4edda; color: #155724; }
.acamp-badge--off { background: #e9ecef; color: #6c757d; }
.acamp-muted { font-size: 0.82rem; color: #aaa; }
.acamp-cycle { font-size: 0.82rem; color: #555; }
.acamp-days-left { color: #e67e22; font-weight: 700; }

/* Buttons */
.acamp-btn { padding: 8px 18px; border: none; border-radius: 6px; cursor: pointer; font-size: 0.88rem; font-weight: 600; transition: opacity .2s; }
.acamp-btn:disabled { opacity: .6; cursor: not-allowed; }
.acamp-btn--primary  { background: #2E3A59; color: #fff; }
.acamp-btn--primary:hover { opacity: .85; }
.acamp-btn--ghost  { background: #e9ecef; color: #495057; }
.acamp-btn--ghost:hover { background: #dee2e6; }
.acamp-btn--toggle { background: #fff3cd; color: #856404; }
.acamp-btn--toggle:hover { background: #ffeeba; }
.acamp-btn--danger { background: #f8d7da; color: #721c24; }
.acamp-btn--danger:hover { background: #f5c6cb; }
.acamp-btn--sm { padding: 5px 12px; font-size: 0.82rem; }

/* Modal */
.acamp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); z-index: 2000; display: flex; align-items: center; justify-content: center; }
.acamp-modal { background: #fff; border-radius: 12px; width: 100%; max-width: 480px; box-shadow: 0 8px 40px rgba(0,0,0,.18); overflow: hidden; }
.acamp-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: #f4f6f8; border-bottom: 1px solid #eaeaea; }
.acamp-modal__header h2 { margin: 0; font-size: 1.1rem; color: #2c3e50; }
.acamp-modal__close { background: none; border: none; font-size: 1.1rem; cursor: pointer; color: #7f8c8d; }
.acamp-modal__body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.acamp-modal__footer { padding: 14px 20px; display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid #eaeaea; }
.acamp-field { display: flex; flex-direction: column; gap: 6px; }
.acamp-field label { font-size: 0.88rem; font-weight: 600; color: #444; }
.acamp-req { color: #e74c3c; }
.acamp-input { padding: 9px 12px; border: 1px solid #ccc; border-radius: 6px; font-size: 0.9rem; outline: none; transition: border .2s; }
.acamp-input:focus { border-color: #2E3A59; }
.acamp-row { display: flex; align-items: center; gap: 6px; }
.acamp-suffix { font-size: 0.95rem; font-weight: 600; color: #444; white-space: nowrap; }
.acamp-err { color: #e74c3c; font-size: 0.82rem; margin: 0; }
.acamp-check { display: flex; align-items: flex-start; gap: 8px; font-size: 0.88rem; color: #444; cursor: pointer; }
.acamp-check input { margin-top: 2px; cursor: pointer; }
.acamp-preview { background: #eaf4fb; border-radius: 6px; padding: 10px 14px; font-size: 0.88rem; color: #1a5276; font-weight: 600; display: flex; flex-direction: column; gap: 4px; }
.acamp-preview-sub { font-size: 0.8rem; color: #2471a3; font-weight: 400; }
</style>
